import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { cn, formatDate } from '../../lib/utils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * Which grid the popover is showing. Clicking the caption drills up
 * (days → months → years); picking a value drills back down.
 */
type CalendarView = 'days' | 'months' | 'years';

const MONTH_LABELS = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

/** Years per page — 3 columns x 4 rows, the same shape as the month grid. */
const YEARS_PER_PAGE = 12;

const yearPageStart = (year: number) => Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

/** Months since year 0, so month bounds compare as plain numbers. */
const monthOrdinal = (date: Date) => date.getFullYear() * 12 + date.getMonth();

export function DatePicker({
	value,
	onChange,
	placeholder = 'Pick a date',
	variant = 'default',
	minDate,
	clearable = false,
	disabled = false,
	startMonth,
	endMonth,
	defaultMonth,
}: {
	value?: Date | null;
	onChange: (date: Date | undefined) => void;
	placeholder?: string;
	/** "default" for forms (taller, matches Input), "compact" for filter panels */
	variant?: 'default' | 'compact';
	/** Disable all dates before this date */
	minDate?: Date;
	/** Show an X to reset the value back to empty once a date is set */
	clearable?: boolean;
	/** Read-only: the value still shows, it just can't be changed. */
	disabled?: boolean;
	/** Earliest selectable month; also bounds the month and year grids. */
	startMonth?: Date;
	/** Latest selectable month; also bounds the month and year grids. */
	endMonth?: Date;
	/** Month the calendar opens on when there is no value yet. */
	defaultMonth?: Date;
}) {
	const [open, setOpen] = useState(false);
	const [view, setView] = useState<CalendarView>('days');
	const [month, setMonth] = useState<Date>(() => value ?? defaultMonth ?? new Date());

	const showClear = clearable && !!value && !disabled;

	/**
	 * Reset on open rather than in an effect: `defaultMonth` is typically built
	 * inline by the caller, so a new reference each render would make an effect
	 * fire continuously and yank the calendar back while the user is browsing.
	 */
	const handleOpenChange = (next: boolean) => {
		if (disabled) return;
		if (next) {
			setView('days');
			setMonth(value ?? defaultMonth ?? new Date());
		}
		setOpen(next);
	};

	const minOrdinal = useMemo(() => {
		const lower = startMonth ?? minDate;
		return lower ? monthOrdinal(lower) : -Infinity;
	}, [startMonth, minDate]);
	const maxOrdinal = useMemo(() => (endMonth ? monthOrdinal(endMonth) : Infinity), [endMonth]);

	const isMonthDisabled = (year: number, monthIndex: number) => {
		const ordinal = year * 12 + monthIndex;
		return ordinal < minOrdinal || ordinal > maxOrdinal;
	};
	// A year is out only when every one of its months is.
	const isYearDisabled = (year: number) => year * 12 + 11 < minOrdinal || year * 12 > maxOrdinal;

	const pageStart = yearPageStart(month.getFullYear());
	const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);

	return (
		<Popover open={open && !disabled} onOpenChange={handleOpenChange}>
			<div className="relative w-full">
				<PopoverTrigger
					disabled={disabled}
					className={cn(
						'flex w-full items-center gap-2 outline-none transition-colors',
						disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
						variant === 'compact'
							? 'h-7 rounded-lg border border-line-2 bg-surface-input px-2.5 text-xs hover:border-line-3'
							: 'h-8 rounded-lg border border-line-2 bg-surface-input px-2.5 text-sm hover:bg-surface-input/50',
						value ? 'text-text-2' : 'text-text-4',
						showClear && 'pr-8'
					)}
				>
					<CalendarIcon size={12} className="shrink-0 text-text-4" />
					{value ? formatDate(value) : placeholder}
				</PopoverTrigger>
				{showClear && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onChange(undefined);
						}}
						aria-label="Clear date"
						className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
					>
						<X size={12} />
					</button>
				)}
			</div>
			{/*
			 * Fixed width: the day view is sized by its caption row, not the 7-column
			 * grid, so it would otherwise change width both between views and between
			 * months ("May" vs "September"). Pinning the popover and letting each view
			 * fill it keeps the box still.
			 */}
			<PopoverContent align="start" className="w-[210px] p-0">
				{view === 'days' ? (
					<Calendar
						mode="single"
						selected={value ?? undefined}
						onSelect={(date) => {
							onChange(date);
							setOpen(false);
						}}
						disabled={minDate ? { before: minDate } : undefined}
						month={month}
						onMonthChange={setMonth}
						classNames={{ root: 'w-full' }}
						startMonth={startMonth}
						endMonth={endMonth}
						components={{
							CaptionLabel: ({ children }: { children?: ReactNode }) => (
								<button
									type="button"
									onClick={() => setView('months')}
									/*
									 * relative + z-10 is load-bearing: Calendar's nav is
									 * `absolute inset-x-0` across this same row, and a
									 * positioned element paints over in-flow content
									 * whatever the DOM order, so a static button here
									 * never receives the click.
									 */
									className="relative z-10 flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-surface-hover"
								>
									{children}
									<ChevronDown className="size-3 text-muted-foreground" />
								</button>
							),
						}}
					/>
				) : (
					<GridView
						/* Same width as the 7-column day grid, so drilling up and
						   back down never resizes the popover. */
						header={
							view === 'months' ? (
								<GridHeader
									label={String(month.getFullYear())}
									onLabelClick={() => setView('years')}
									onPrev={() =>
										setMonth(
											new Date(month.getFullYear() - 1, month.getMonth(), 1)
										)
									}
									onNext={() =>
										setMonth(
											new Date(month.getFullYear() + 1, month.getMonth(), 1)
										)
									}
									prevDisabled={isYearDisabled(month.getFullYear() - 1)}
									nextDisabled={isYearDisabled(month.getFullYear() + 1)}
								/>
							) : (
								<GridHeader
									label={`${pageStart} – ${pageStart + YEARS_PER_PAGE - 1}`}
									onPrev={() =>
										setMonth(
											new Date(
												month.getFullYear() - YEARS_PER_PAGE,
												month.getMonth(),
												1
											)
										)
									}
									onNext={() =>
										setMonth(
											new Date(
												month.getFullYear() + YEARS_PER_PAGE,
												month.getMonth(),
												1
											)
										)
									}
									prevDisabled={years.every((y) =>
										isYearDisabled(y - YEARS_PER_PAGE)
									)}
									nextDisabled={years.every((y) =>
										isYearDisabled(y + YEARS_PER_PAGE)
									)}
								/>
							)
						}
					>
						{view === 'months'
							? MONTH_LABELS.map((label, index) => (
									<GridCell
										key={label}
										label={label}
										selected={
											value?.getFullYear() === month.getFullYear() &&
											value?.getMonth() === index
										}
										disabled={isMonthDisabled(month.getFullYear(), index)}
										onClick={() => {
											setMonth(new Date(month.getFullYear(), index, 1));
											setView('days');
										}}
									/>
								))
							: years.map((year) => (
									<GridCell
										key={year}
										label={String(year)}
										selected={value?.getFullYear() === year}
										disabled={isYearDisabled(year)}
										onClick={() => {
											setMonth(new Date(year, month.getMonth(), 1));
											setView('months');
										}}
									/>
								))}
					</GridView>
				)}
			</PopoverContent>
		</Popover>
	);
}

/** Shell for the month and year grids, matched to the day grid's footprint. */
function GridView({ header, children }: { header: ReactNode; children: ReactNode }) {
	return (
		/* No background: PopoverContent supplies the surface, and Calendar goes
		   transparent inside one. Painting bg-background here would put the app
		   canvas colour over the popover's. */
		<div className="w-full [--cell-size:--spacing(6)] p-2 text-xs">
			{header}
			<div className="mt-2 grid grid-cols-3 gap-1">{children}</div>
		</div>
	);
}

/** Paging row: matches the day grid's nav, with a label that drills up further. */
function GridHeader({
	label,
	onLabelClick,
	onPrev,
	onNext,
	prevDisabled,
	nextDisabled,
}: {
	label: string;
	onLabelClick?: () => void;
	onPrev: () => void;
	onNext: () => void;
	prevDisabled?: boolean;
	nextDisabled?: boolean;
}) {
	const arrow =
		'flex size-(--cell-size) cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:pointer-events-none disabled:opacity-50';

	return (
		<div className="flex items-center justify-between gap-1">
			<button type="button" onClick={onPrev} disabled={prevDisabled} className={arrow}>
				<ChevronLeft className="size-3.5" />
			</button>
			{onLabelClick ? (
				<button
					type="button"
					onClick={onLabelClick}
					className="flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-surface-hover"
				>
					{label}
					<ChevronDown className="size-3 text-muted-foreground" />
				</button>
			) : (
				<span className="px-1.5 text-xs font-medium select-none">{label}</span>
			)}
			<button type="button" onClick={onNext} disabled={nextDisabled} className={arrow}>
				<ChevronRight className="size-3.5" />
			</button>
		</div>
	);
}

function GridCell({
	label,
	selected,
	disabled,
	onClick,
}: {
	label: string;
	selected?: boolean;
	disabled?: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={cn(
				'flex h-6 cursor-pointer items-center justify-center rounded-md text-2xs transition-colors',
				selected
					? 'bg-primary text-primary-foreground'
					: 'text-text-2 hover:bg-surface-hover hover:text-text-1',
				disabled && 'pointer-events-none opacity-40'
			)}
		>
			{label}
		</button>
	);
}
